import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, RefreshCw, Bot, GraduationCap } from "lucide-react";

export default function AIKnowledgeTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [teaching, setTeaching] = useState({}); // { [id]: string }

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/pending-knowledge");
      setRows(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const resolve = async (r, withAnswer = false) => {
    const officialAnswer = withAnswer ? (teaching[r.id] || "").trim() : "";
    if (withAnswer && !officialAnswer) {
      toast.error("Isi dulu jawaban resmi yang mau diajarkan ke Arpi.");
      return;
    }
    try {
      await api.put(`/admin/pending-knowledge/${r.id}/resolve`, {
        official_answer: officialAnswer,
      });
      toast.success(officialAnswer ? "Diajarkan ke Arpi ✓" : "Ditandai selesai");
      setTeaching((t) => ({ ...t, [r.id]: "" }));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const badge = (reason) =>
    reason === "escalated" ? (
      <span className="px-2 py-0.5 text-[10px] tracking-wider bg-red-100 text-red-700">ESKALASI</span>
    ) : (
      <span className="px-2 py-0.5 text-[10px] tracking-wider bg-stone-100 text-stone-600">POLA NON-MITRA</span>
    );

  return (
    <div className="space-y-5" data-testid="ai-knowledge-tab">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <p className="text-sm text-stone-600 flex items-center gap-2">
          <Bot size={16} className="text-[#F39237]" />
          Pertanyaan yang belum terjawab AI. Isi <strong>Jawaban Resmi</strong> untuk mengajari Arpi — jawaban akan
          langsung dipakai oleh <em>@arpi_master_bot</em> maupun bot Arpi live.
        </p>
        <button
          onClick={load}
          data-testid="ai-knowledge-refresh"
          className="inline-flex items-center gap-2 text-sm text-stone-600 hover:text-[#F39237]"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className="space-y-3">
        {loading ? (
          <div className="px-4 py-10 text-center text-stone-500 border border-stone-200 bg-white">Memuat…</div>
        ) : rows.length === 0 ? (
          <div className="px-4 py-10 text-center text-stone-500 border border-stone-200 bg-white">
            Belum ada data pembelajaran AI.
          </div>
        ) : (
          rows.map((r) => (
            <div
              key={r.id}
              className={`border border-stone-200 bg-white p-4 ${r.resolved ? "opacity-60" : ""}`}
              data-testid={`ai-knowledge-row-${r.id}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-xs text-stone-500">
                  <span className="font-medium text-stone-700">
                    {r.username ? `@${r.username}` : r.first_name || "-"}
                  </span>
                  {r.is_mitra && <span className="text-[#F39237]">Mitra {r.tier}</span>}
                  {badge(r.reason)}
                  <span>{new Date(r.created_at).toLocaleDateString("id-ID")}</span>
                  {r.official_answer && (
                    <span className="px-2 py-0.5 text-[10px] tracking-wider bg-green-100 text-green-700">
                      SUDAH DIAJARKAN
                    </span>
                  )}
                </div>
              </div>
              <div className="mt-2 text-sm">
                <div className="text-stone-800"><strong>Q:</strong> {r.question}</div>
                <div className="text-stone-500 mt-1"><strong>A (AI):</strong> {r.answer}</div>
                {r.official_answer && (
                  <div className="text-green-700 mt-1"><strong>A (Resmi):</strong> {r.official_answer}</div>
                )}
              </div>

              {!r.resolved && (
                <div className="mt-3 space-y-2">
                  <textarea
                    value={teaching[r.id] || ""}
                    onChange={(e) => setTeaching((t) => ({ ...t, [r.id]: e.target.value }))}
                    placeholder="Tulis jawaban resmi untuk mengajari Arpi (opsional)…"
                    data-testid={`ai-knowledge-teach-input-${r.id}`}
                    className="w-full border border-stone-300 px-3 py-2 text-sm min-h-[70px] focus:outline-none focus:border-[#F39237]"
                  />
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => resolve(r, true)}
                      data-testid={`ai-knowledge-teach-${r.id}`}
                      className="inline-flex items-center gap-1.5 text-xs text-white bg-[#0A0A0A] hover:bg-[#F39237] px-3 py-1.5"
                    >
                      <GraduationCap size={14} /> Ajarkan &amp; Selesai
                    </button>
                    <button
                      onClick={() => resolve(r, false)}
                      data-testid={`ai-knowledge-resolve-${r.id}`}
                      className="inline-flex items-center gap-1.5 text-xs text-green-700 hover:text-green-900"
                    >
                      <CheckCircle2 size={14} /> Tandai selesai saja
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
